import React, {useState, useEffect, useMemo, useRef, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  InputAdornment,
  Typography,
  InputLabel,
  IconButton,
  Button,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

const containsText = function (text, searchText) {
  return (
    String(text ?? '')
      .toLowerCase()
      .indexOf(String(searchText ?? '').toLowerCase()) > -1
  );
};

const optionLabel = function (option) {
  // Prefer `label` (canonical); fall back to legacy `text` used by some callers.
  return option?.label ?? option?.text ?? '';
};

const allNoneButtonSx = {
  textTransform: 'none',
  color: '#aaa',
  borderColor: '#ccc',
  minWidth: 'auto',
  fontSize: '11px',
  lineHeight: '13px',
};

function IntelliSearchSelect({
  name,
  id,
  value,
  options,
  disabled,
  onChange,
  className,
  label,
  allNoneEnabled = false,
  returnFullEvent = false,
  multiple = false,
  onOpenFocusSearch = false,
  placeholder = 'Select...',
  optionDisplayLimit = 4,
  getValueLabel = null,
  renderValue: renderValueProp,
  selectSx,
  matchAnchorWidth = false,
}) {
  const {t} = useTranslation();
  const formControlRef = useRef(null);
  const [anchorWidth, setAnchorWidth] = useState(undefined);
  const [searchText, setSearchText] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!matchAnchorWidth || !formControlRef.current) {
      return undefined;
    }
    const el = formControlRef.current;
    const updateWidth = () => {
      return setAnchorWidth(el.offsetWidth);
    };
    updateWidth();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(updateWidth);
      ro.observe(el);
      return () => {
        return ro.disconnect();
      };
    }
    window.addEventListener('resize', updateWidth);
    return () => {
      return window.removeEventListener('resize', updateWidth);
    };
  }, [matchAnchorWidth]);

  const groups = useMemo(() => {
    const uniqueGroups = [
      ...new Set(
        (options || []).map((option) => {
          return option.group || '';
        })
      ),
    ];
    // special handling to sort 'Not Assigned' group to the end
    uniqueGroups.sort((a, b) => {
      if (a === 'Not Assigned') {
        return 1;
      }
      if (b === 'Not Assigned') {
        return -1;
      }
      return a.localeCompare(b);
    });
    return uniqueGroups;
  }, [options]);

  const displayedOptions = useMemo(() => {
    if (!options) {
      return [];
    }
    return options.filter((option) => {
      return containsText(optionLabel(option), searchText);
    });
  }, [searchText, options]);

  const missingSelectedOptions = useMemo(() => {
    if (!multiple || !Array.isArray(value) || value.length === 0) {
      return [];
    }
    const knownValues = new Set(
      (options || []).map((opt) => {
        return String(opt.value);
      })
    );
    return value
      .filter((v) => {
        return !knownValues.has(String(v));
      })
      .map((v) => {
        const resolved = typeof getValueLabel === 'function' ? getValueLabel(v) : null;
        return {value: v, label: resolved != null && resolved !== '' ? resolved : String(v), missing: true};
      });
  }, [multiple, value, options, getValueLabel]);

  const handleChange = useCallback(
    (event) => {
      if (returnFullEvent) {
        onChange(event);
      } else {
        onChange(event.target.value);
      }
    },
    [onChange, returnFullEvent]
  );

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    setSearchText('');
    // With autoFocus: false the select keeps focus the whole time the menu is
    // open, so its focus ring survives the close and reads as a stuck border.
    // Blur it once MUI finishes its close-focus handling — scoped to this
    // control so we never blur something else the user just clicked.
    requestAnimationFrame(() => {
      const root = formControlRef.current;
      const active = document.activeElement;
      if (root && active && root.contains(active)) {
        active.blur();
      }
    });
  }, []);

  const allNoneHeader = useMemo(() => {
    if (!menuOpen || !allNoneEnabled || !multiple) {
      return null;
    }
    return (
      <ListSubheader>
        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', padding: '3px'}}>
          <Button
            size='small'
            variant='outlined'
            className='font-weight-normal mr-1 '
            sx={{
              ...allNoneButtonSx,
              '&:hover': {
                color: '#62c462',
                borderColor: '#62c462',
              },
            }}
            onClick={() => {
              const currentlySelectedOptions = value || [];
              const allOptionValues = displayedOptions
                .filter((option) => {
                  return !option.disabled;
                })
                .map((option) => {
                  return option.value;
                });
              const newOptions = [...new Set([...currentlySelectedOptions, ...allOptionValues])];
              if (allOptionValues.length > 0) {
                handleChange({target: {name: name, value: newOptions}});
              }
            }}
          >
            {t('All')}
          </Button>
          <Button
            size='small'
            variant='outlined'
            className='font-weight-normal'
            sx={{
              ...allNoneButtonSx,
              '&:hover': {
                color: '#ee5f5b',
                borderColor: '#ee5f5b',
              },
            }}
            onClick={() => {
              const currentlySelectedOptions = value;
              const displayedOptionsValues = displayedOptions.map((option) => {
                return option.value;
              });
              const missingValues =
                searchText === ''
                  ? missingSelectedOptions.map((option) => {
                      return option.value;
                    })
                  : [];
              const toRemove = new Set([...displayedOptionsValues, ...missingValues]);
              const newOptions = currentlySelectedOptions.filter((option) => {
                return !toRemove.has(option);
              });
              handleChange({target: {name: name, value: newOptions}});
            }}
          >
            {t('None')}
          </Button>
        </div>
      </ListSubheader>
    );
  }, [
    menuOpen,
    allNoneEnabled,
    multiple,
    value,
    displayedOptions,
    searchText,
    missingSelectedOptions,
    handleChange,
    name,
    t,
  ]);

  const missingOptionItems = useMemo(() => {
    if (!menuOpen || missingSelectedOptions.length === 0) {
      return null;
    }
    return missingSelectedOptions.map((option) => {
      const stableKey = `__missing__::${String(option.value)}`;
      return (
        <MenuItem key={stableKey} value={option.value}>
          {optionLabel(option)}
        </MenuItem>
      );
    });
  }, [menuOpen, missingSelectedOptions]);

  const optionMenuItems = useMemo(() => {
    if (!menuOpen) {
      return null;
    }

    if (groups.length > 0 && !(groups.length == 1 && groups[0] == '')) {
      const optionsByGroup = {};
      groups.forEach((group) => {
        optionsByGroup[group] = [];
      });
      displayedOptions.forEach((option) => {
        optionsByGroup[option.group || ''].push(option);
      });

      const nonEmptyGroupNames = Object.keys(optionsByGroup).filter((group) => {
        return optionsByGroup[group].length > 0;
      });

      return nonEmptyGroupNames.map((group) => {
        const subheaderElement = (
          <ListSubheader key={`${group}::header`}>
            <div className='d-flex justify-content-between'>
              <span>{group}</span>
              <div>
                <Button
                  size='small'
                  variant='outlined'
                  className='font-weight-normal mr-1 '
                  sx={{
                    ...allNoneButtonSx,
                    '&:hover': {
                      color: '#62c462',
                      borderColor: '#62c462',
                    },
                  }}
                  onClick={() => {
                    const groupOptions = optionsByGroup[group]
                      .filter((option) => {
                        return !option.disabled;
                      })
                      .map((option) => {
                        return option.value;
                      });
                    const selectedOptions = [...new Set([...value, ...groupOptions])];
                    handleChange({target: {name: name, value: selectedOptions}});
                  }}
                >
                  {t('All')}
                </Button>
                <Button
                  size='small'
                  variant='outlined'
                  className='font-weight-normal'
                  sx={{
                    ...allNoneButtonSx,
                    '&:hover': {
                      color: '#ee5f5b',
                      borderColor: '#ee5f5b',
                    },
                  }}
                  onClick={() => {
                    const groupOptions = optionsByGroup[group].map((option) => {
                      return option.value;
                    });
                    const selectedOptions = value.filter((item) => {
                      return !groupOptions.includes(item);
                    });
                    handleChange({target: {name: name, value: selectedOptions}});
                  }}
                >
                  {t('None')}
                </Button>
              </div>
            </div>
          </ListSubheader>
        );

        const optionsRenderElementList = optionsByGroup[group].map((option, optionIdx) => {
          const displayLabel = optionLabel(option);
          const stableKey = `${group}::${String(option.value)}::${displayLabel}::${optionIdx}`;
          return (
            <MenuItem key={stableKey} value={option.value} disabled={option.disabled}>
              {displayLabel}
            </MenuItem>
          );
        });

        return [subheaderElement, ...optionsRenderElementList];
      });
    }

    return displayedOptions.map((option, optionIdx) => {
      const displayLabel = optionLabel(option);
      const stableKey = `${String(option.value)}::${displayLabel}::${optionIdx}`;
      return (
        <MenuItem key={stableKey} value={option.value} disabled={option.disabled}>
          {displayLabel}
        </MenuItem>
      );
    });
  }, [menuOpen, groups, displayedOptions, value, handleChange, name, t]);

  const menuProps = useMemo(() => {
    return {
      autoFocus: false,
      // Keep the menu inside the viewport.
      //
      // The height cap used to sit on the inner list, which left the Paper itself unbounded: MUI
      // sizes and flips the Paper against the viewport, so an unbounded Paper opening near the
      // bottom of the window ran off the edge and the last options were unreachable. Capping the
      // Paper - and letting it be the thing that scrolls - lets MUI do its collision maths
      // against the real height.
      marginThreshold: 8,
      PaperProps: {
        sx: {
          maxHeight: 'min(60vh, 420px)',
          overflowY: 'auto',
          ...(matchAnchorWidth && anchorWidth ? {maxWidth: anchorWidth, width: anchorWidth} : {}),
          ...(matchAnchorWidth && anchorWidth
            ? {'& .MuiMenuItem-root': {whiteSpace: 'normal', wordBreak: 'break-word'}}
            : {}),
        },
      },
      sx: {
        zIndex: 10000,
        '& .MuiList-root': {
          paddingTop: 0,
          height: 'auto',
        },
        '& .MuiListSubheader-root': {
          padding: 1,
          paddingBottom: 0,
        },
        '& .MuiMenuItem-root': {
          paddingY: '2px',
        },
      },
    };
  }, [matchAnchorWidth, anchorWidth]);

  return (
    <FormControl fullWidth ref={formControlRef}>
      {label && (
        <InputLabel sx={{maxWidth: 'none'}} shrink>
          {label}
        </InputLabel>
      )}
      <Select
        MenuProps={menuProps}
        sx={{backgroundColor: 'white', ...(selectSx || {})}}
        id={id}
        name={name}
        value={value}
        label={label}
        className={className}
        multiple={multiple}
        disabled={disabled}
        size='small'
        onChange={handleChange}
        onOpen={() => {
          return setMenuOpen(true);
        }}
        onClose={handleMenuClose}
        displayEmpty
        renderValue={(renderedValue) => {
          if (renderValueProp) {
            return renderValueProp(renderedValue, options, {placeholder, multiple});
          }
          if (!multiple && renderedValue !== '' && renderedValue !== undefined && renderedValue !== null) {
            const valueOption = options.find((option) => {
              return option.value == renderedValue || String(option.value) === String(renderedValue);
            });
            if (valueOption) {
              return optionLabel(valueOption);
            }
          }
          if (multiple && renderedValue && renderedValue.length > 0) {
            const valueOptions = renderedValue.map((singleValue) => {
              const valueOption = options.find((option) => {
                return option.value == singleValue || String(option.value) === String(singleValue);
              });
              if (valueOption) {
                return optionLabel(valueOption);
              }
              const resolved = typeof getValueLabel === 'function' ? getValueLabel(singleValue) : null;
              return resolved != null && resolved !== '' ? resolved : String(singleValue);
            });
            return valueOptions.length > optionDisplayLimit
              ? t('{{count}} options selected', {count: valueOptions.length})
              : valueOptions.join(', ');
          }
          return <Typography color='gray'>{placeholder}</Typography>;
        }}
      >
        <ListSubheader>
          <TextField
            size='small'
            autoFocus={onOpenFocusSearch}
            placeholder={t('Type to search...')}
            fullWidth
            value={searchText}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <IconButton
                  onClick={() => {
                    return setSearchText('');
                  }}
                >
                  <ClearIcon fontSize='small' />
                </IconButton>
              ),
            }}
            onChange={(e) => {
              return setSearchText(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        {allNoneHeader}
        {missingOptionItems}
        {optionMenuItems}
      </Select>
    </FormControl>
  );
}

export {IntelliSearchSelect};
